import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import classNames from 'classnames';

import { useTranslate } from "../../../../../i18n/useTranslationModules";
import { getDefaultAgendaImage } from "../../../../../store/utils/create-event";
import { getGoogleMeetRoom } from "../../../../../store/actions/event/breakout-rooms";
import { BreakoutRoom, Session, GoogleMeetVideoSettings } from "../../../../../types/working-model";
import { resizedImage } from "../../../../../utils/utils";
import { getStorageItem } from '../../../../../utils/local-storage';
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import ModalComponent from "../../../../general-ui/modal/modal";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import Clock from "../../../../../components/general-ui/clock/clock";
import { DisableZoomRoom } from "../../../../../connection/zoom";
import '../../../../../scss/live-event/base/session/breakout-rooms/room-card.scss';
import { showAlert } from "../../../../general-ui/alert/alert-service";
import { useGoogleLogin } from "@react-oauth/google";
import { StartCallResult } from "../../../../../types/meet";

interface Props {
	room: BreakoutRoom;
	template: string;
	ended?: boolean;
	checkingEnded?: boolean;
	onJoinClick?: (...args: any[]) => any;
	session: Session;
	closeBreakoutRoom: (...args: any[]) => any;
	isBreakoutSessionHost: (...args: any[]) => boolean;
	submitGoogleToken?: (token: string, url: string) => Promise<StartCallResult>;
	joinDisabled?: boolean;
}

const RoomCard: React.FC<Props> = ({
	room,
	template,
	ended,
	checkingEnded,
	onJoinClick,
	session,
	closeBreakoutRoom,
	isBreakoutSessionHost,
	submitGoogleToken,
	joinDisabled = false,
}) => {
	const { t } = useTranslate("session");
	const [closingRoom, setClosingRoom] = useState<boolean>(false);
	const [googleBreakoutRoom, setGoogleBreakoutRoom] = useState<BreakoutRoom | null>(null);
	const [gettingGoogleRoomInfo, setGettingGoogleRoomInfo] = useState(false);
	const [openEndRoomModal, setOpenEndRoomModal] = useState(false);

	const sessionStartDate = new Date(session.timestamp as number);
	const sessionEndDate = new Date(session.end_timestamp as number);
	const useZoom = !!session.breakout_session?.use_zoom;
	const useGoogle = !!session.breakout_session?.use_google_meet;
	const createNewRoomWhenFull = !!session?.breakout_session?.create_room_when_full;
	const token = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const event = eventBundle || workingEvent;
	const disableSessionThumbnails = event?.settings?.disable_session_thumbnails;


	const dispatch = useDispatch();

	// Rerender the component every 10 seconds to keep card buttons accurate
	const [/* time */, setTime] = useState(Date.now());
	useEffect(() => {
		const interval = setInterval(() => setTime(Date.now()), 10000);
		return () => {
			clearInterval(interval);
		};
	}, []);

	// Rooms marked as closing should remain in a state of closing until the updated room arrives
	useEffect(() => {
		setClosingRoom(false);
	}, [room]);

	const roomUsage = useTypedSelector(state => state.BreakoutRoomsReducer.room_usage);
	const eventUuid = useTypedSelector(state => state.LiveEventReducer.eventBundle?.uuid);
	const closingAllRooms = useTypedSelector(state => state.BreakoutRoomsReducer.closing_all_rooms);

	// get the local storage data of the booted user, if exists
	const isBooted = useCallback(() => {
		return eventUuid && getStorageItem(`booted.${eventUuid}`) as any;
	}, [eventUuid]) as any;

	const isBootedFromRoom = useCallback(() => {
		const isUserBooted = isBooted();

		if (!isUserBooted) return false;
		if (isUserBooted?.type === 'session') return true;
		if (!roomUsage?.data) return false;
		if (room.uuid !== isUserBooted?.breakoutUuid) return false;

		// loop through room data and if the opentok id from the booted storage item exists,
		// then return true so we can disable the room card join button
		let matchedOpentokId = false;
		for (const key in roomUsage.data) {
			const rooms = JSON.parse(roomUsage.data[key])?.rooms;
			if (!rooms) continue;
			if (rooms?.[isUserBooted?.opentok_id] !== undefined) {
				matchedOpentokId = true;
				break;
			}
		}
		return matchedOpentokId;
	}, [roomUsage, isBooted, room]);

	const cardBackgroundImage = (image: string | undefined) => {
		// if thumbnails disabled, return nothing
		if (disableSessionThumbnails) {
			return '';
		}

		// Use image if the user uploaded one for the room. If not, use the default template image background
		if (image) {
			return `linear-gradient(rgba(0,0,0,0.0), rgba(0,0,0,0.5)), url(${resizedImage(image, { width: 720 })})`;
		} else {
			return `linear-gradient(rgba(0,0,0,0.0), rgba(0,0,0,0.5)), url(${getDefaultAgendaImage(template, 0)})`;
		}
	};

	const getIsEnded = () => {
		//marked as ended from hv api
		if (ended) {
			return ended;
		}
		//marked as ended in published version
		else if (room.video_settings?.zoom?.enabled === false
			|| (sessionEndDate && (Date.now() >= sessionEndDate.getTime()))) {
			return true;
		}
		return false;
	};

	const roomCardBtnDisabled = () => {
		if (getIsEnded() || checkingEnded || room.closed) {
			return true;
		}
		// User can join room 10m within start time
		else if (sessionStartDate.getTime() - Date.now() >= (60000 * 10)) {
			return true;
		} else {
			return false;
		}
	};

	const roomSizeAndUsers = useCallback(() => {
		let roomSize = room.max_users;
		let totalUsers = 0;

		if (roomUsage?.data?.[room.uuid]) {
			const data = JSON.parse(roomUsage.data[room.uuid]);
			roomSize = data?.limit || room.max_users;
			if (data?.rooms) {
				totalUsers = Object.values(data.rooms).reduce((accum: number, curr: any): number => {
					return accum + curr;
				}, 0);
			}
		}
		return { roomSize, totalUsers };
	}, [room.max_users, room.uuid, roomUsage?.data]);

	const { totalUsers, roomSize } = roomSizeAndUsers();

	const roomCardBtnText = () => {
		if (isBootedFromRoom()) {
			return t("Room Unavailable");
		}
		if (!getIsEnded() && room.closed) {
			return t("Room Unavailable");
		}
		if (getIsEnded()) {
			return t("Session Complete");
		}
		// User can join room 10m within start time
		if (sessionStartDate.getTime() - Date.now() >= (60000 * 10)) {
			const timeOptions = { timeStyle: "short" } as Intl.DateTimeFormatOptions;
			return `${t("Opens at")} ${sessionStartDate.toLocaleTimeString(undefined, timeOptions)}`;
		}
		// if room full and "create new room when full" is off
		if (totalUsers >= roomSize && !createNewRoomWhenFull) {
			return t("Room is full");
		}
		return t("Join");
	};

	const options = {
		client_id: process.env.REACT_APP_MEET_INTEGRATION_OAUTH_CLIENT_ID,
		scope: 'https://www.googleapis.com/auth/meetings.host',
		include_granted_scopes: true,
		redirect_uri: window.location.origin,
		onSuccess: async (credentialResponse: { access_token: string }) => {
			let joinResult: StartCallResult | undefined;
			if (submitGoogleToken) {
				joinResult = await submitGoogleToken(credentialResponse.access_token, room.video_settings?.google_meet?.meetingUri);
			}
			if (
				joinResult === StartCallResult.STARTING
				&& onJoinClick
			) {
				onJoinClick(room);
			}
		},
		onError: () => {
			console.log('Login Failed');
			showAlert({
				message: "Login Failed",
				description: "There was a problem logging into your Google account, please try again late",
				type: "error",
				duration: 5000
			});
		},
	};

	const googleLogin = useGoogleLogin(options);

	const openGoogleMeet = useCallback(async (room: BreakoutRoom) => {
		if (room.roomAtCapacity) {
			showAlert({
				message: "Room Full",
				description: "This room is full, please try later",
				type: "error",
				duration: 10000
			});
		} else {
			try {
				const videoSettings: GoogleMeetVideoSettings | undefined = room?.video_settings?.google_meet;
				if (!videoSettings?.meetingUri) throw new Error('no meeting uri');
				googleLogin();
			} catch (e) {
				console.error(e);
				showAlert({
					message: "Unable to launch Google Meet",
					description: "Unknown error occured launching Google Meet; no Meet URL found",
					type: "error",
					duration: 5000
				});
			}
		}
	}, [googleLogin]);

	const handleGoogleMeetJoinClick = async (room: BreakoutRoom) => {
		if (!token) return;

		setGettingGoogleRoomInfo(true);
		setGoogleBreakoutRoom(null);
		const sessionUuid = session.uuid;
		const breakoutUuid = room.uuid;
		dispatch(getGoogleMeetRoom(sessionUuid, breakoutUuid, token));
	};
	const goToBreakout = async () => {
		// clear the previous opentok id from session storage so we don't try and rejoin that room,
		// the value is set to the new opentok id once the user enters the room in order to try and reconnect them to that opentok id if they page refresh
		sessionStorage.removeItem(`rejoin.breakout.${room.uuid}`);

		if (useZoom && room.video_settings.zoom.join_url) {
			// Zoom powered BRs. Open up link to Zoom meeting.
			window.open(room.video_settings.zoom.join_url, '_blank');
		} else if (useGoogle) {
			handleGoogleMeetJoinClick(room);
		} else if (onJoinClick) {
			// Vonage/BL solution. Open up preview modal for user to set camera/mic before joining.
			onJoinClick(room);
		}
	};


	// Open the Google Meeting only after information about the Google Meet has reloaded.
	// The information should re-load for EVERY join click, before the user is allowed to join the meet.
	// This allows for checking capacity and that the meet is 'active'.
	// To allow for only running this function on room change only, track BreakoutRoom (room) as internal state
	//   which is cleared before dispatch to get BreakoutRoom information. 
	useEffect(() => { if (useGoogle) setGoogleBreakoutRoom(room); }, [room, useGoogle]);
	useEffect(() => {
		if (!gettingGoogleRoomInfo || !googleBreakoutRoom?.video_settings?.google_meet?.meetingUri) return;

		setGettingGoogleRoomInfo(false);
		openGoogleMeet(googleBreakoutRoom);
	}, [googleBreakoutRoom, gettingGoogleRoomInfo, openGoogleMeet]);

	const renderBreakoutRoomUsage = () => {
		return "";

		//removing until we can fix the count
		return (
			<div className={classNames("user-counts", template)}>{roomSize} {t("Room Size")} | {totalUsers} {t("Total Users")}</div>
		);
	};

	const handleCloseEndRoomModal = () => {
		setOpenEndRoomModal(false);
	};

	const _closeBreakoutRoom = () => {
		setClosingRoom(true);
		closeBreakoutRoom(room.session, room.id || 0, () => setClosingRoom(false));
		handleCloseEndRoomModal();
	};

	const handleEndRoom = () => {
		setOpenEndRoomModal(true);
	};

	const checkEnd = () => {
		const isOver = Date.now() >= sessionEndDate.getTime() + (90 * 60000);
		if (token && isOver)
			DisableZoomRoom(room.video_settings.zoom.id, token);
	};

	return (
		<div
			key={room.id}
			className={classNames("room-card", template, { 'no-image': disableSessionThumbnails })}
			style={{ backgroundImage: cardBackgroundImage(room.image) }}
		>
			<div><span className={`name ${template}`}>{room.name}</span></div>
			<div><span className={`description ${template}`}>{room.description}</span></div>
			{useZoom && <div className="zoom-kickout-time">{<Clock onMinute={checkEnd} />}</div>
			}
			<div className="actions-container">
				<button
					onClick={goToBreakout}
					className={`join-btn margin-right`}
					disabled={
						isBootedFromRoom()
						|| isBooted()?.type === 'session'
						|| roomCardBtnDisabled()
						|| (totalUsers >= roomSize && !createNewRoomWhenFull)
						|| joinDisabled
					}
				>
					<span>{checkingEnded || gettingGoogleRoomInfo ? <WaitingIndicator /> : roomCardBtnText()}</span>
				</button>
				{isBreakoutSessionHost(room.session) && !getIsEnded() && !room.closed && !useGoogle &&
					<button className={`join-btn`} disabled={closingRoom || closingAllRooms} onClick={handleEndRoom}>
						<span>{closingRoom || closingAllRooms ? <WaitingIndicator /> : t('Close Room')}</span>
					</button>}
				{
					!isBootedFromRoom()
						&& !useZoom
						&& !roomCardBtnDisabled()
						? renderBreakoutRoomUsage()
						: null}
			</div>

			<ModalComponent
				open={openEndRoomModal}
				onRequestClose={handleCloseEndRoomModal}
				cancellable
				closeable
				title={t("Close Room")}
				footer={(
					<>
						<button onClick={handleCloseEndRoomModal}>Cancel</button>
						<button onClick={_closeBreakoutRoom} className="primary">Yes</button>
					</>
				)}
			>
				<div>
					<TypographyItem>{t("Are you sure you want to close")} {room.name}?</TypographyItem>
					<TypographyItem>{t("This action cannot be undone!")}</TypographyItem>
				</div>
			</ModalComponent>
		</div >
	);
};

export default RoomCard;